import { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  align?: 'left' | 'center' | 'right';
  render?: (row: T) => React.ReactNode;
  accessor?: (row: T) => any;
  width?: string | number;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  defaultRowsPerPage?: number;
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  onRowClick?: (row: T) => void;
  className?: string;
}

type SortDir = 'asc' | 'desc' | null;

function getValue(row: any, col: Column<any>): any {
  return col.accessor ? col.accessor(row) : row[col.key];
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function compareValues(a: any, b: any): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  const strA = String(a);
  const strB = String(b);

  if (DATE_RE.test(strA) && DATE_RE.test(strB)) {
    return strA.localeCompare(strB);
  }

  const numA = typeof a === 'string' ? parseFloat(a) : a;
  const numB = typeof b === 'string' ? parseFloat(b) : b;

  if (typeof numA === 'number' && !isNaN(numA) && typeof numB === 'number' && !isNaN(numB)) {
    return numA - numB;
  }

  return strA.localeCompare(strB, undefined, { sensitivity: 'base' });
}

export default function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data available',
  defaultRowsPerPage = 25,
  defaultSortKey,
  defaultSortDir,
  onRowClick,
  className = '',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir ?? null);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const sortColumn = useMemo(
    () => columns.find((c) => c.key === sortKey) ?? null,
    [columns, sortKey],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortKey(null);
        setSortDir(null);
      } else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const searchableColumns = useMemo(() => columns.filter((c) => c.filterable), [columns]);

  const filteredData = useMemo(() => {
    let result = Array.isArray(data) ? data : [];

    const term = search.trim().toLowerCase();
    if (term && searchableColumns.length > 0) {
      result = result.filter((row) =>
        searchableColumns.some((col) => {
          const val = getValue(row, col);
          return val != null && String(val).toLowerCase().includes(term);
        }),
      );
    }

    if (sortKey && sortDir && sortColumn) {
      result = [...result];
      result.sort((a, b) => {
        const cmp = compareValues(getValue(a, sortColumn), getValue(b, sortColumn));
        return sortDir === 'desc' ? -cmp : cmp;
      });
    }

    return result;
  }, [data, search, searchableColumns, sortKey, sortDir, sortColumn]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const pagedData = filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const searchPlaceholder =
    searchableColumns.length > 0
      ? `Search ${searchableColumns.map((c) => c.header).join(' / ')}...`
      : 'Search...';

  if (loading) {
    return (
      <div className={`overflow-x-auto ${className}`}>
        <table className="table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td key={col.key}>
                    <div className="skeleton h-4 w-full rounded" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={className}>
      {searchableColumns.length > 0 && (
        <div className="flex items-center justify-end mb-2">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--color-text-muted)' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
              className="input text-xs"
              style={{ minWidth: '240px', padding: '6px 8px 6px 28px' }}
            />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table
          className="table"
          style={
            columns.some((c) => c.width != null)
              ? { tableLayout: 'fixed', width: '100%' }
              : undefined
          }
        >
          {columns.some((c) => c.width != null) && (
            <colgroup>
              {columns.map((col) => (
                <col
                  key={col.key}
                  style={{
                    width:
                      col.width != null
                        ? typeof col.width === 'number'
                          ? `${col.width}px`
                          : col.width
                        : undefined,
                  }}
                />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${
                    col.sortable ? 'cursor-pointer select-none hover:bg-opacity-80' : ''
                  }`}
                  style={{ position: 'sticky', top: 0, zIndex: 1 }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span
                    className={`flex items-center gap-1 ${
                      col.align === 'right'
                        ? 'justify-end'
                        : col.align === 'center'
                          ? 'justify-center'
                          : 'justify-start'
                    }`}
                  >
                    {col.header}
                    {col.sortable && (
                      <span className="inline-flex flex-col ml-1" style={{ lineHeight: 0 }}>
                        {sortKey === col.key ? (
                          sortDir === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronsUpDown size={14} style={{ opacity: 0.3 }} />
                        )}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-8"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pagedData.map((row, idx) => (
                <tr
                  key={idx}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'cursor-pointer' : ''}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : ''
                      }
                    >
                      {col.render ? col.render(row) : (getValue(row, col) ?? '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredData.length > 10 && (
        <div
          className="flex items-center justify-between mt-3 px-1 text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="input py-1 px-2 w-auto text-xs"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredData.length)} of{' '}
              {filteredData.length}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn btn-secondary py-1 px-2 text-xs disabled:opacity-30"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn btn-secondary py-1 px-2 text-xs disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
